import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createOrganisationInvite,
  createOrganisationInvites,
  completeMetricThresholdReview,
  completeResultsMappingReview,
  dismissActivationChecklist,
  dismissAppAnnouncement,
  getActivationChecklist,
  getPendingAppAnnouncementState,
  queryOrganisationInvites,
  revokeOrganisationInvite,
  setActivationChecklistTaskCompletion,
  syncOrganisationMembers,
} from '@/features/users/data';
import {
  changePassword,
  deleteUser,
  getUser,
  queryUsers,
  resendEmailVerification,
  removeUserFromOrganisation,
  updateUser,
  updateUserContext,
  updateUserRole,
} from '@/features/users/data';
import {
  gridPaths,
  invalidateGridQuery,
  invalidateOrganisationInvites,
  invalidateOrganisations,
  invalidateUserDetail,
  invalidateUsers,
  queryKeys,
  useInvalidateQueries,
  useIsAuthenticated,
} from '@/shared/hooks';
import { coreWakeReconnectQueryOptions } from '@/shared/hooks/queryLifecyclePolicies';
import { removeFromResult } from '@/shared/hooks/shared';
export { CURRENT_ORG_ROLE_INVALIDATED_EVENT } from '@/shared/hooks/useCurrentOrgRole';
import { CURRENT_ORG_ROLE_INVALIDATED_EVENT } from '@/shared/hooks/useCurrentOrgRole';
import type { PendingAppAnnouncementState } from '@/features/users/data';
import type { InviteBatchResult } from '@/features/users/data/invites';
import type { ApiError } from '@/shared/data/client';
import type {
  ActivationChecklistDismissalDto,
  ActivationChecklistDto,
  ActivationChecklistTaskCompletionDto,
  ActivationChecklistTaskToggleDto,
  ActivationTaskKey,
  OrganisationMembershipBulkSyncDto,
  OrganisationInviteDto,
  OrganisationRole,
  QueryResult,
  UserDto,
  UserListItemDto,
} from '@flowqa/shared';

type UpdateUserInput = { id: string; email?: string; firstName?: string; lastName?: string };
type DeleteUserInput = { id: string; password: string };
type ChangePasswordInput = { id: string; currentPassword: string; newPassword: string };
type UpdateUserRoleInput = { id: string; role: OrganisationRole };
type RemoveUserMembershipInput = { id: string };
type InviteUserInput = { organisationId: string; email: string; role?: OrganisationRole };
type InviteUsersInput = { organisationId: string; emails: string[]; role?: OrganisationRole };
type ActivationChecklistTaskToggle = { taskKey: ActivationTaskKey; completed: boolean };

export const useUsersQuery = (options?: {
  enabled?: boolean;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  refetchIntervalMs?: number | false;
  organisationId?: string | null;
}): UseQueryResult<QueryResult<UserListItemDto>, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const organisationId = options?.organisationId ?? selectedOrganisationId ?? null;
  return useQuery<QueryResult<UserListItemDto>, ApiError>({
    ...coreWakeReconnectQueryOptions,
    queryKey: queryKeys.users(organisationId),
    enabled: isAuthenticated && (options?.enabled ?? true),
    queryFn: async (): Promise<QueryResult<UserListItemDto>> =>
      await queryUsers(organisationId ?? undefined),
    staleTime: options?.staleTimeMs ?? 5 * 60_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchInterval: options?.refetchIntervalMs ?? false,
  });
};

export const useUserDetailQuery = (id: string | null): UseQueryResult<UserDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  return useQuery<UserDto | null, ApiError>({
    ...coreWakeReconnectQueryOptions,
    queryKey: queryKeys.userDetail(id),
    enabled: isAuthenticated && Boolean(id),
    queryFn: async (): Promise<UserDto | null> => (id ? await getUser(id) : null),
    staleTime: 5 * 60_000,
  });
};

export const useUpdateUser = (): UseMutationResult<
  UserDto,
  ApiError,
  UpdateUserInput,
  { previousUsers?: QueryResult<UserListItemDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const usersKey = queryKeys.users(selectedOrganisationId ?? null);

  return useMutation<
    UserDto,
    ApiError,
    UpdateUserInput,
    { previousUsers?: QueryResult<UserListItemDto> }
  >({
    mutationFn: async (input: UpdateUserInput): Promise<UserDto> => {
      return await updateUser(input);
    },
    onMutate: (input) => {
      const previousUsers = queryClient.getQueryData<QueryResult<UserListItemDto>>(usersKey);
      if (previousUsers) {
        queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((user) =>
                  user.id === input.id ? { ...user, ...input } : user,
                ),
              }
            : prev,
        );
      }
      return { previousUsers };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(usersKey, context.previousUsers);
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((user) =>
                user.id === updated.id
                  ? {
                      ...user,
                      email: updated.email,
                      firstName: updated.firstName,
                      lastName: updated.lastName,
                    }
                  : user,
              ),
            }
          : prev,
      );
      await invalidateQueries([
        invalidateUsers(selectedOrganisationId ?? null),
        invalidateUserDetail(updated.id),
        invalidateGridQuery(gridPaths.users),
      ]);
    },
  });
};

export const useDeleteUser = (): UseMutationResult<
  { id: string },
  ApiError,
  DeleteUserInput,
  { previousUsers?: QueryResult<UserListItemDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const usersKey = queryKeys.users(selectedOrganisationId ?? null);

  return useMutation<
    { id: string },
    ApiError,
    DeleteUserInput,
    { previousUsers?: QueryResult<UserListItemDto> }
  >({
    mutationFn: async (input: DeleteUserInput): Promise<{ id: string }> => {
      return await deleteUser(input);
    },
    onMutate: (input) => {
      const previousUsers = queryClient.getQueryData<QueryResult<UserListItemDto>>(usersKey);
      if (previousUsers) {
        queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
          removeFromResult(prev, input.id),
        );
      }
      return { previousUsers };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(usersKey, context.previousUsers);
      }
    },
    onSuccess: async (deleted) => {
      queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
        removeFromResult(prev, deleted.id),
      );
      await invalidateQueries([invalidateUsers(), invalidateGridQuery(gridPaths.users)]);
    },
  });
};

export const useUpdateUserRole = (): UseMutationResult<
  { status: string; role: OrganisationRole; id: string },
  ApiError,
  UpdateUserRoleInput,
  { previousUsers?: QueryResult<UserListItemDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { refreshSession, session } = useAuth();
  const currentUserId = session?.userId ?? null;
  const usersKey = queryKeys.users(selectedOrganisationId ?? null);

  return useMutation<
    { status: string; role: OrganisationRole; id: string },
    ApiError,
    UpdateUserRoleInput,
    { previousUsers?: QueryResult<UserListItemDto> }
  >({
    mutationFn: async (
      input: UpdateUserRoleInput,
    ): Promise<{ status: string; role: OrganisationRole; id: string }> => {
      const result = await updateUserRole(input);
      return { ...result, id: input.id };
    },
    onMutate: (input) => {
      const previousUsers = queryClient.getQueryData<QueryResult<UserListItemDto>>(usersKey);
      if (previousUsers) {
        queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((user) =>
                  user.id === input.id ? { ...user, role: input.role } : user,
                ),
              }
            : prev,
        );
      }
      return { previousUsers };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(usersKey, context.previousUsers);
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((user) =>
                user.id === updated.id ? { ...user, role: updated.role } : user,
              ),
            }
          : prev,
      );
      await invalidateQueries([
        invalidateUsers(selectedOrganisationId ?? null),
        invalidateGridQuery(gridPaths.users),
      ]);
      if (updated.id === currentUserId) {
        queryClient.removeQueries({ queryKey: queryKeys.currentUserRolePrefix() });
        window.dispatchEvent(new CustomEvent(CURRENT_ORG_ROLE_INVALIDATED_EVENT));
      }
      void refreshSession();
    },
  });
};

export const useUpdateUserContext = (): UseMutationResult<
  {
    lastOrganisationId: string | null;
    lastProjectId: string | null;
    onboardingCompletedAt: string | null;
  },
  ApiError,
  {
    lastOrganisationId?: string | null;
    lastProjectId?: string | null;
    onboardingCompletedAt?: string | null;
  }
> =>
  useMutation({
    mutationFn: async (
      input,
    ): Promise<{
      lastOrganisationId: string | null;
      lastProjectId: string | null;
      onboardingCompletedAt: string | null;
    }> => await updateUserContext(input),
  });

export const usePendingAppAnnouncementState = (options?: {
  enabled?: boolean;
}): UseQueryResult<PendingAppAnnouncementState, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { session } = useAuth();
  const userId = session?.userId ?? null;
  return useQuery<PendingAppAnnouncementState, ApiError>({
    ...coreWakeReconnectQueryOptions,
    queryKey: queryKeys.userAppAnnouncementsPending(userId),
    enabled: isAuthenticated && userId !== null && (options?.enabled ?? true),
    queryFn: async (): Promise<PendingAppAnnouncementState> =>
      await getPendingAppAnnouncementState(),
    staleTime: 30_000,
  });
};

export const useActivationChecklist = (options?: {
  enabled?: boolean;
}): UseQueryResult<ActivationChecklistDto, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { session } = useAuth();
  const userId = session?.userId ?? null;
  const organisationId = session?.orgId ?? null;
  const projectId = session?.selectedProjectId ?? session?.lastProjectId ?? null;
  return useQuery<ActivationChecklistDto, ApiError>({
    ...coreWakeReconnectQueryOptions,
    queryKey: queryKeys.userActivationChecklist(userId, organisationId, projectId),
    enabled: isAuthenticated && userId !== null && (options?.enabled ?? true),
    queryFn: getActivationChecklist,
    staleTime: 0,
  });
};

export const useDismissActivationChecklist = (): UseMutationResult<
  ActivationChecklistDismissalDto,
  ApiError,
  undefined
> => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const checklistKey = queryKeys.userActivationChecklist(
    session?.userId ?? null,
    session?.orgId ?? null,
    session?.selectedProjectId ?? session?.lastProjectId ?? null,
  );

  return useMutation<ActivationChecklistDismissalDto, ApiError, undefined>({
    mutationFn: dismissActivationChecklist,
    onSuccess: () => {
      queryClient.setQueryData<ActivationChecklistDto>(checklistKey, (previous) =>
        previous ? { ...previous, dismissed: true } : previous,
      );
    },
  });
};

export const useSetActivationChecklistTaskCompletion = (): UseMutationResult<
  ActivationChecklistTaskToggleDto,
  ApiError,
  ActivationChecklistTaskToggle,
  { previousChecklist?: ActivationChecklistDto }
> => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const projectId = session?.selectedProjectId ?? session?.lastProjectId ?? null;
  const checklistKey = queryKeys.userActivationChecklist(
    session?.userId ?? null,
    session?.orgId ?? null,
    projectId,
  );

  return useMutation<
    ActivationChecklistTaskToggleDto,
    ApiError,
    ActivationChecklistTaskToggle,
    { previousChecklist?: ActivationChecklistDto }
  >({
    mutationFn: async (input): Promise<ActivationChecklistTaskToggleDto> =>
      await setActivationChecklistTaskCompletion({ ...input, projectId }),
    onMutate: (input) => {
      const previousChecklist = queryClient.getQueryData<ActivationChecklistDto>(checklistKey);
      queryClient.setQueryData<ActivationChecklistDto>(checklistKey, (previous) => {
        if (!previous) return previous;
        const items = previous.items.map((item) =>
          item.key === input.taskKey ? { ...item, completed: input.completed } : item,
        );
        return {
          ...previous,
          items,
          completedCount: items.filter((item) => item.completed).length,
        };
      });
      return { previousChecklist };
    },
    onError: (_error, _input, context) => {
      if (context?.previousChecklist) {
        queryClient.setQueryData(checklistKey, context.previousChecklist);
      }
    },
  });
};

export const useCompleteMetricThresholdReview = (): UseMutationResult<
  ActivationChecklistTaskCompletionDto,
  ApiError,
  string
> => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const checklistKey = queryKeys.userActivationChecklist(
    session?.userId ?? null,
    session?.orgId ?? null,
    session?.selectedProjectId ?? session?.lastProjectId ?? null,
  );

  return useMutation<ActivationChecklistTaskCompletionDto, ApiError, string>({
    mutationFn: completeMetricThresholdReview,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: checklistKey });
    },
  });
};

export const useCompleteResultsMappingReview = (): UseMutationResult<
  ActivationChecklistTaskCompletionDto,
  ApiError,
  string
> => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const checklistKey = queryKeys.userActivationChecklist(
    session?.userId ?? null,
    session?.orgId ?? null,
    session?.selectedProjectId ?? session?.lastProjectId ?? null,
  );

  return useMutation<ActivationChecklistTaskCompletionDto, ApiError, string>({
    mutationFn: completeResultsMappingReview,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: checklistKey });
    },
  });
};

export const useDismissAppAnnouncement = (): UseMutationResult<
  { announcementKey: string; dismissedAt: string },
  ApiError,
  string
> => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation<{ announcementKey: string; dismissedAt: string }, ApiError, string>({
    mutationFn: async (
      announcementKey: string,
    ): Promise<{
      announcementKey: string;
      dismissedAt: string;
    }> => await dismissAppAnnouncement(announcementKey),
    onSuccess: () => {
      queryClient.setQueryData<PendingAppAnnouncementState>(
        queryKeys.userAppAnnouncementsPending(session?.userId ?? null),
        (previous) =>
          previous
            ? {
                ...previous,
                pendingAnnouncementKey: null,
              }
            : {
                pendingAnnouncementKey: null,
              },
      );
    },
  });
};

export const useResendEmailVerification = (): UseMutationResult<
  { status: string },
  ApiError,
  void
> =>
  useMutation({
    mutationFn: async (): Promise<{ status: string }> => await resendEmailVerification(),
  });

export const useRemoveUserFromOrganisation = (): UseMutationResult<
  { id: string },
  ApiError,
  RemoveUserMembershipInput,
  { previousUsers?: QueryResult<UserListItemDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { refreshSession, session } = useAuth();
  const currentUserId = session?.userId ?? null;
  const usersKey = queryKeys.users(selectedOrganisationId ?? null);

  return useMutation<
    { id: string },
    ApiError,
    RemoveUserMembershipInput,
    { previousUsers?: QueryResult<UserListItemDto> }
  >({
    mutationFn: async (input: RemoveUserMembershipInput): Promise<{ id: string }> => {
      return await removeUserFromOrganisation(input);
    },
    onMutate: (input) => {
      const previousUsers = queryClient.getQueryData<QueryResult<UserListItemDto>>(usersKey);
      if (previousUsers) {
        queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
          removeFromResult(prev, input.id),
        );
      }
      return { previousUsers };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(usersKey, context.previousUsers);
      }
    },
    onSuccess: async (removed) => {
      queryClient.setQueryData<QueryResult<UserListItemDto>>(usersKey, (prev) =>
        removeFromResult(prev, removed.id),
      );
      await invalidateQueries([
        invalidateUsers(selectedOrganisationId ?? null),
        invalidateGridQuery(gridPaths.users),
      ]);
      if (removed.id === currentUserId) {
        queryClient.removeQueries({ queryKey: queryKeys.currentUserRolePrefix() });
        window.dispatchEvent(new CustomEvent(CURRENT_ORG_ROLE_INVALIDATED_EVENT));
      }
      void refreshSession();
    },
  });
};

export const useInvalidateUsers = (): (() => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useCallback(async (): Promise<void> => {
    await invalidateQueries([
      invalidateUsers(selectedOrganisationId ?? null),
      invalidateGridQuery(gridPaths.users),
    ]);
  }, [invalidateQueries, selectedOrganisationId]);
};

export const useInvalidateUserDetail = (): ((id: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  return useCallback(
    async (id: string | null): Promise<void> => {
      await invalidateQueries([invalidateUserDetail(id)]);
    },
    [invalidateQueries],
  );
};

export const useChangePassword = (): UseMutationResult<
  { status: string },
  ApiError,
  ChangePasswordInput
> => {
  return useMutation<{ status: string }, ApiError, ChangePasswordInput>({
    mutationFn: async (input: ChangePasswordInput) => await changePassword(input),
  });
};

export const useOrganisationInvitesQuery = (options?: {
  enabled?: boolean;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  refetchIntervalMs?: number | false;
}): UseQueryResult<OrganisationInviteDto[], ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useQuery<OrganisationInviteDto[], ApiError>({
    ...coreWakeReconnectQueryOptions,
    queryKey: queryKeys.organisationInvites(selectedOrganisationId ?? null),
    enabled: isAuthenticated && Boolean(selectedOrganisationId) && (options?.enabled ?? true),
    queryFn: async (): Promise<OrganisationInviteDto[]> =>
      await queryOrganisationInvites({ organisationId: selectedOrganisationId ?? '' }),
    staleTime: options?.staleTimeMs ?? 30_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
    refetchInterval: options?.refetchIntervalMs ?? false,
  });
};

export const useInviteUser = (): UseMutationResult<
  OrganisationInviteDto,
  ApiError,
  InviteUserInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useMutation<OrganisationInviteDto, ApiError, InviteUserInput>({
    mutationFn: async (input: InviteUserInput): Promise<OrganisationInviteDto> =>
      await createOrganisationInvite(input),
    onSettled: async () => {
      await invalidateQueries([
        invalidateOrganisationInvites(selectedOrganisationId ?? null),
        invalidateUsers(selectedOrganisationId ?? null),
        invalidateGridQuery(gridPaths.users),
      ]);
    },
  });
};

export const useInviteUsers = (): UseMutationResult<
  InviteBatchResult,
  ApiError,
  InviteUsersInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useMutation<InviteBatchResult, ApiError, InviteUsersInput>({
    mutationFn: async (input: InviteUsersInput): Promise<InviteBatchResult> =>
      await createOrganisationInvites(input),
    onSettled: async () => {
      await invalidateQueries([
        invalidateOrganisationInvites(selectedOrganisationId ?? null),
        invalidateUsers(selectedOrganisationId ?? null),
        invalidateGridQuery(gridPaths.users),
      ]);
    },
  });
};

export const useRevokeOrganisationInvite = (): UseMutationResult<
  OrganisationInviteDto,
  ApiError,
  { organisationId: string; invitationId: string }
> => {
  const invalidateQueries = useInvalidateQueries();
  return useMutation<
    OrganisationInviteDto,
    ApiError,
    { organisationId: string; invitationId: string }
  >({
    mutationFn: async (input): Promise<OrganisationInviteDto> =>
      await revokeOrganisationInvite(input),
    onSettled: async (_invite, _error, input) => {
      await invalidateQueries([
        invalidateOrganisationInvites(input.organisationId),
        invalidateUsers(input.organisationId),
        invalidateGridQuery(gridPaths.users),
      ]);
    },
  });
};

export const useSyncOrganisationMembers = (): UseMutationResult<
  OrganisationMembershipBulkSyncDto,
  ApiError,
  { organisationId: string }
> => {
  const invalidateQueries = useInvalidateQueries();
  return useMutation<OrganisationMembershipBulkSyncDto, ApiError, { organisationId: string }>({
    mutationFn: async (input): Promise<OrganisationMembershipBulkSyncDto> =>
      await syncOrganisationMembers(input),
    onSuccess: async (result) => {
      await invalidateQueries([
        invalidateOrganisationInvites(result.organisationId),
        invalidateUsers(result.organisationId),
        invalidateGridQuery(gridPaths.users),
        invalidateOrganisations(),
      ]);
    },
  });
};
